import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { Spinner, Table, TextInput } from 'flowbite-react';
import { HiOutlineSearch } from 'react-icons/hi';
import Title from '../../../components/Title';
import Button from '../../../components/Button';
import PaginationFlowBite from '../../../components/PaginationFlowBite';
import { PRINT_SHOP_PER_PAGE_OPTIONS } from '../../../components/paginationItems';
import DefaultModal from '../../../components/modals/DefaultModal';
import { useActorEmail } from '../../../hooks/useActorEmail';
import {
  bulkAdvanceRmJobs,
  DEFAULT_RM_PAGE_SIZE,
  fetchRmJobCounts,
  fetchRmJobs,
  fetchRmLaneJobs,
} from './api';
import {
  confirmNames,
  customizationDisplay,
  dateCellLines,
  emptyLaneHint,
  emptyLaneHintText,
  flipOrder,
  formatLabel,
  isSearchTooBroadError,
  issueLabel,
  issueWindowNote,
  laneLayout,
  LaneLayout,
  refusalMessage,
  RmTabKey,
  sourceChipClass,
  sourceLabel,
  stageLabel,
} from './helpers';
import { formatMessage, rmQueueStrings } from './strings';
import {
  RM_STAGE_ORDER,
  RmAdvanceStage,
  RmBulkAdvanceResponse,
  RmBulkAdvanceResult,
  RmPrintJobRow,
  RmPrintStage,
  RmQueueOrder,
} from './types';

const SEARCH_DEBOUNCE_MS = 300;
/** The batch move's own cap on ids per request (DS `RM_PRINT_BULK_MAX_JOBS`). */
const BULK_MAX_JOBS = 200;
const { queue } = rmQueueStrings;

const TAB_ORDER: RmTabKey[] = [...RM_STAGE_ORDER, 'all'];

/** A batch move's outcome, kept until dismissed or the lane changes. */
interface BulkOutcome {
  toStage: RmAdvanceStage;
  moved: number;
  refused: Array<{ jobId: string; name: string; message: string }>;
}

// A type guard rather than `result.ok ? … : …`: without strictNullChecks a
// boolean-literal discriminant does not narrow.
const isRefused = (
  result: RmBulkAdvanceResult,
): result is Extract<RmBulkAdvanceResult, { ok: false }> => result.ok === false;

const toOutcome = (
  response: RmBulkAdvanceResponse,
  names: Map<string, string>,
): BulkOutcome => ({
  toStage: response.toStage,
  moved: response.ok,
  refused: response.results.filter(isRefused).map((result) => ({
    jobId: result.jobId,
    name: names.get(result.jobId) ?? result.jobId,
    message: refusalMessage(result.code, result.message),
  })),
});

export default function RmPrintQueue() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const actorEmail = useActorEmail();

  const [activeTab, setActiveTab] = useState<RmTabKey>('sent_to_print');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_RM_PAGE_SIZE);
  // Unset until the operator flips the date header: DS then applies the
  // lane's own default, so a tab switch always lands on that default.
  const [order, setOrder] = useState<RmQueueOrder | undefined>(undefined);
  // jobId → member name, so the confirm and the outcome can name who moved.
  const [selected, setSelected] = useState<Map<string, string>>(new Map());
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [outcome, setOutcome] = useState<BulkOutcome | null>(null);
  const [selectingAll, setSelectingAll] = useState(false);
  const [selectAllFailed, setSelectAllFailed] = useState(false);

  // Debounce the raw input into the committed query, and reset to page one so a
  // narrowed search never leaves the operator stranded on a page that no longer
  // exists.
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput);
      setPage(1);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // A different search is a different set of jobs: the selection goes with it.
  useEffect(() => {
    setSelected(new Map());
  }, [search]);

  const stageParam = activeTab === 'all' ? undefined : activeTab;
  const layout = laneLayout(activeTab);
  const shownOrder = order ?? layout.defaultOrder;

  const { data, isLoading, isError, error, isFetching, refetch } = useQuery(
    ['rm-print-jobs', activeTab, search, page, pageSize, order],
    () =>
      fetchRmJobs({ stage: stageParam, search, page, limit: pageSize, order }),
    { keepPreviousData: true, refetchOnWindowFocus: false, retry: false },
  );

  const { data: counts, refetch: refetchCounts } = useQuery(
    ['rm-print-jobs-counts', search],
    () => fetchRmJobCounts(search),
    { refetchOnWindowFocus: false, retry: false },
  );

  const jobs = data?.data ?? [];
  const meta = data?.meta;

  const searchTooBroad = useMemo(
    () => isError && isSearchTooBroadError(error),
    [isError, error],
  );

  const bulkMutation = useMutation(
    ({ ids, to }: { ids: string[]; to: RmAdvanceStage }) =>
      bulkAdvanceRmJobs(ids, to, actorEmail),
    {
      onSuccess: (response) => {
        setOutcome(toOutcome(response, selected));
        setSelected(new Map());
        setConfirmOpen(false);
        queryClient.invalidateQueries('rm-print-jobs');
        queryClient.invalidateQueries('rm-print-jobs-counts');
      },
      // DS commits each job separately, so a timeout or 5xx can arrive after
      // some of them moved: reload what the lane really holds.
      onError: () => {
        queryClient.invalidateQueries('rm-print-jobs');
        queryClient.invalidateQueries('rm-print-jobs-counts');
      },
    },
  );

  const tabCount = (tab: RmTabKey): number | undefined => {
    if (!counts) {
      return undefined;
    }
    return tab === 'all' ? counts.total : counts[tab];
  };

  const selectTab = (tab: RmTabKey) => {
    setActiveTab(tab);
    setOrder(undefined);
    setPage(1);
    setSelected(new Map());
    setOutcome(null);
    setSelectAllFailed(false);
  };

  const onFlipOrder = () => {
    setOrder(flipOrder(shownOrder));
    setPage(1);
  };

  const onRefresh = () => {
    refetch();
    refetchCounts();
  };

  const toggleRow = (job: RmPrintJobRow) => {
    setSelected((previous) => {
      const next = new Map(previous);
      if (next.has(job.jobId)) {
        next.delete(job.jobId);
      } else {
        next.set(job.jobId, job.member.name);
      }
      return next;
    });
  };

  const pageAllSelected =
    jobs.length > 0 && jobs.every((job) => selected.has(job.jobId));

  const togglePage = () => {
    setSelected((previous) => {
      const next = new Map(previous);
      jobs.forEach((job) => {
        if (pageAllSelected) {
          next.delete(job.jobId);
        } else {
          next.set(job.jobId, job.member.name);
        }
      });
      return next;
    });
  };

  const laneTotal = meta?.total ?? 0;
  const canSelectAll =
    layout.bulkTarget !== null &&
    selected.size > 0 &&
    selected.size < laneTotal &&
    laneTotal <= BULK_MAX_JOBS;

  const selectAll = async () => {
    if (!stageParam) {
      return;
    }
    setSelectingAll(true);
    setSelectAllFailed(false);
    try {
      const rows = await fetchRmLaneJobs(stageParam, search, BULK_MAX_JOBS);
      setSelected(new Map(rows.map((job) => [job.jobId, job.member.name])));
    } catch {
      setSelectAllFailed(true);
    } finally {
      setSelectingAll(false);
    }
  };

  const refusedById = useMemo(
    () => new Map((outcome?.refused ?? []).map((row) => [row.jobId, row])),
    [outcome],
  );

  const hint = !isLoading && !isError && jobs.length === 0
    ? emptyLaneHint(activeTab, counts)
    : null;

  return (
    <div className="pt-8 min-h-screen w-full">
      <div className="mb-8 flex items-center justify-between">
        <div>
          <Title>{queue.pageTitle}</Title>
          {counts?.cutoffDay !== undefined && (
            <p
              data-testid="rm-cutoff-note"
              className="mt-1 text-xs text-neutral-500"
            >
              {issueWindowNote(new Date(), counts.cutoffDay)}
            </p>
          )}
        </div>
        <Button
          variant="OUTLINED"
          isProcessing={isFetching && !isLoading}
          onClick={onRefresh}
        >
          {queue.refresh}
        </Button>
      </div>

      <div className="shadow-md bg-white rounded-lg">
        <div className="flex flex-wrap border-b border-neutral-100 px-2">
          {TAB_ORDER.map((tab) => {
            const isActive = tab === activeTab;
            const label =
              tab === 'all' ? queue.tabAll : stageLabel(tab as RmPrintStage);
            const count = tabCount(tab);
            return (
              <button
                key={tab}
                type="button"
                data-testid={`rm-tab-${tab}`}
                aria-pressed={isActive}
                onClick={() => selectTab(tab)}
                className={`flex items-center gap-2 border-b-2 px-4 py-3 text-sm font-medium ${
                  isActive
                    ? 'border-primary-500 text-primary-600'
                    : 'border-transparent text-neutral-500 hover:text-neutral-700'
                }`}
              >
                {label}
                {count !== undefined && (
                  <span
                    data-testid={`rm-tab-count-${tab}`}
                    className="rounded-full bg-neutral-100 px-2 py-0.5 text-xs text-neutral-600"
                  >
                    {count}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        <div className="flex flex-wrap items-center gap-3 border-b border-neutral-100 px-4 py-3">
          <TextInput
            data-testid="rm-search"
            icon={HiOutlineSearch}
            placeholder={queue.searchPlaceholder}
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="w-full max-w-[400px]"
          />
          {searchTooBroad && (
            <span
              data-testid="rm-search-too-broad"
              className="text-sm text-amber-600"
            >
              {queue.searchTooBroad}
            </span>
          )}
        </div>

        {layout.bulkTarget && selected.size > 0 && (
          <SelectionBar
            count={selected.size}
            target={layout.bulkTarget}
            canSelectAll={canSelectAll}
            laneTotal={laneTotal}
            selectingAll={selectingAll}
            selectAllFailed={selectAllFailed}
            tooMany={selected.size > BULK_MAX_JOBS}
            onClear={() => setSelected(new Map())}
            onSelectAll={selectAll}
            onMark={() => setConfirmOpen(true)}
          />
        )}

        {outcome && (
          <BulkOutcomeBanner
            outcome={outcome}
            onDismiss={() => setOutcome(null)}
          />
        )}

        {isError && !searchTooBroad && (
          <div
            data-testid="rm-queue-error"
            className="px-4 py-10 text-center text-neutral-500"
          >
            {queue.error}
          </div>
        )}

        {isLoading && (
          <div className="flex justify-center px-4 py-10">
            <Spinner data-testid="rm-queue-loading" />
          </div>
        )}

        {!isLoading && !isError && jobs.length === 0 && (
          <div
            data-testid="rm-queue-empty"
            className="px-4 py-10 text-center text-neutral-500"
          >
            {hint ? (
              <div className="flex flex-col items-center gap-3">
                <span data-testid="rm-empty-lane-hint">
                  {emptyLaneHintText(hint)}
                </span>
                <div className="flex gap-2">
                  {hint.map(({ lane, count }) => (
                    <Button
                      key={lane}
                      variant="OUTLINED"
                      data-testid={`rm-empty-lane-show-${lane}`}
                      onClick={() => selectTab(lane)}
                    >
                      {formatMessage(queue.showLane, {
                        lane: stageLabel(lane),
                        count,
                      })}
                    </Button>
                  ))}
                </div>
              </div>
            ) : (
              queue.empty
            )}
          </div>
        )}

        {!isLoading && !isError && jobs.length > 0 && (
          <Table>
            <Table.Head>
              {layout.bulkTarget && (
                <Table.HeadCell className="w-8 px-4 py-3">
                  <input
                    type="checkbox"
                    data-testid="rm-select-page"
                    aria-label={queue.columns.selectPage}
                    checked={pageAllSelected}
                    onChange={togglePage}
                    className="h-4 w-4 accent-primary-500"
                  />
                </Table.HeadCell>
              )}
              <Table.HeadCell className="px-4 py-3">
                {queue.columns.member}
              </Table.HeadCell>
              <Table.HeadCell className="px-4 py-3">
                {queue.columns.issue}
              </Table.HeadCell>
              {layout.showStage && (
                <Table.HeadCell
                  className="px-4 py-3"
                  data-testid="rm-column-stage"
                >
                  {queue.columns.stage}
                </Table.HeadCell>
              )}
              <Table.HeadCell className="px-4 py-3">
                {queue.columns.source}
              </Table.HeadCell>
              <Table.HeadCell className="px-4 py-3">
                {queue.columns.format}
              </Table.HeadCell>
              <Table.HeadCell className="px-4 py-3">
                {queue.columns.customization}
              </Table.HeadCell>
              <Table.HeadCell
                className="whitespace-nowrap px-4 py-3"
                data-testid="rm-column-time"
                aria-sort={shownOrder === 'oldest' ? 'ascending' : 'descending'}
              >
                <button
                  type="button"
                  data-testid="rm-sort-toggle"
                  onClick={onFlipOrder}
                  aria-label={formatMessage(queue.order.flip, {
                    column: queue.columns[layout.timeColumn],
                    order: queue.order[shownOrder],
                  })}
                  className="inline-flex items-center gap-1 uppercase text-primary-700 hover:underline"
                >
                  {queue.columns[layout.timeColumn]}
                  <span aria-hidden="true" className="text-[10px]">
                    {shownOrder === 'oldest' ? '▲' : '▼'}
                  </span>
                  <span
                    data-testid="rm-sort-order"
                    className="ml-1 text-[11px] font-medium normal-case"
                  >
                    {queue.order[shownOrder]}
                  </span>
                </button>
              </Table.HeadCell>
            </Table.Head>
            <Table.Body>
              {jobs.map((job) => (
                <QueueRow
                  key={job.jobId}
                  job={job}
                  layout={layout}
                  checked={selected.has(job.jobId)}
                  refusal={refusedById.get(job.jobId)?.message}
                  onToggle={() => toggleRow(job)}
                  onOpen={() => navigate(`/print-shop/rm/${job.jobId}`)}
                />
              ))}
            </Table.Body>
          </Table>
        )}

        <div
          data-testid="rm-queue-pagination"
          className="border-t border-neutral-100"
        >
          <PaginationFlowBite
            tablePagination={{
              pageSize,
              total: meta?.total ?? 0,
              // From `total`, not `meta.lastPage`, whatever base DS counts it in.
              lastPage: Math.max(1, Math.ceil((meta?.total ?? 0) / pageSize)),
              currentPage: page,
              onChange: (next: number) => setPage(next),
            }}
            perPageOptions={PRINT_SHOP_PER_PAGE_OPTIONS}
            handlePerPage={({ value }) => {
              setPageSize(value);
              setPage(1);
            }}
          />
        </div>
      </div>

      {layout.bulkTarget && (
        <BulkConfirmModal
          open={confirmOpen}
          names={Array.from(selected.values())}
          from={activeTab as RmPrintStage}
          to={layout.bulkTarget}
          submitting={bulkMutation.isLoading}
          failed={bulkMutation.isError}
          onClose={() => {
            if (!bulkMutation.isLoading) {
              setConfirmOpen(false);
              bulkMutation.reset();
            }
          }}
          onConfirm={() =>
            bulkMutation.mutate({
              ids: Array.from(selected.keys()),
              to: layout.bulkTarget as RmAdvanceStage,
            })
          }
        />
      )}
    </div>
  );
}

function QueueRow({
  job,
  layout,
  checked,
  refusal,
  onToggle,
  onOpen,
}: {
  job: RmPrintJobRow;
  layout: LaneLayout;
  checked: boolean;
  /** Why the last batch move left this job where it is. */
  refusal?: string;
  onToggle: () => void;
  onOpen: () => void;
}) {
  const customization = customizationDisplay(job);
  const issue = issueLabel(job.issue.month, job.issue.year);
  const when = dateCellLines(job, layout.dateCell);
  return (
    <Table.Row
      data-testid={`rm-job-row-${job.jobId}`}
      role="link"
      tabIndex={0}
      aria-label={`${job.member.name} — ${issue}`}
      className={`cursor-pointer hover:bg-neutral-50 ${
        checked ? 'bg-primary-50/40' : 'bg-white'
      }`}
      onClick={onOpen}
      onKeyDown={(e) => {
        if (e.target === e.currentTarget && (e.key === 'Enter' || e.key === ' ')) {
          onOpen();
        }
      }}
    >
      {layout.bulkTarget && (
        <Table.Cell
          className="px-4 py-3"
          onClick={(event) => event.stopPropagation()}
        >
          <input
            type="checkbox"
            data-testid={`rm-select-${job.jobId}`}
            aria-label={formatMessage(queue.columns.select, {
              name: job.member.name,
            })}
            checked={checked}
            onChange={onToggle}
            className="h-4 w-4 accent-primary-500"
          />
        </Table.Cell>
      )}
      <Table.Cell className="px-4 py-3">
        <div className="font-medium text-neutral-800">{job.member.name}</div>
        <div className="text-xs text-neutral-500">{job.member.email}</div>
        {refusal && (
          <div
            data-testid={`rm-job-refused-${job.jobId}`}
            className="mt-1 text-xs text-error-700"
          >
            {refusal}
          </div>
        )}
      </Table.Cell>
      <Table.Cell className="whitespace-nowrap px-4 py-3">{issue}</Table.Cell>
      {layout.showStage && (
        <Table.Cell className="px-4 py-3">{stageLabel(job.stage)}</Table.Cell>
      )}
      <Table.Cell className="px-4 py-3">
        <span
          data-testid={`rm-job-source-${job.jobId}`}
          className={`whitespace-nowrap rounded-md px-2 py-0.5 text-xs font-medium ${sourceChipClass(
            job.source,
          )}`}
        >
          {sourceLabel(job.source)}
        </span>
      </Table.Cell>
      <Table.Cell
        className="whitespace-nowrap px-4 py-3"
        data-testid={`rm-job-format-${job.jobId}`}
      >
        {formatLabel(job.format)}
      </Table.Cell>
      <Table.Cell className="px-4 py-3">
        <span
          data-testid={`rm-job-customization-${job.jobId}`}
          title={customization.hint}
        >
          {customization.text}
        </span>
      </Table.Cell>
      <Table.Cell className="whitespace-nowrap px-4 py-3">
        <div
          data-testid={`rm-job-age-${job.jobId}`}
          className={`text-neutral-800 ${
            layout.dateCell === 'age' ? 'font-semibold' : ''
          }`}
        >
          {when.main}
        </div>
        {when.sub && (
          <div
            data-testid={`rm-job-age-sub-${job.jobId}`}
            className="text-xs text-neutral-500"
          >
            {when.sub}
          </div>
        )}
      </Table.Cell>
    </Table.Row>
  );
}

function SelectionBar({
  count,
  target,
  canSelectAll,
  laneTotal,
  selectingAll,
  selectAllFailed,
  tooMany,
  onClear,
  onSelectAll,
  onMark,
}: {
  count: number;
  target: RmAdvanceStage;
  canSelectAll: boolean;
  laneTotal: number;
  selectingAll: boolean;
  selectAllFailed: boolean;
  /** More than one batch move takes: DS would refuse the whole request. */
  tooMany: boolean;
  onClear: () => void;
  onSelectAll: () => void;
  onMark: () => void;
}) {
  const linkClass =
    'text-sm font-semibold text-primary-700 underline disabled:opacity-60';
  return (
    <div
      data-testid="rm-selection-bar"
      className="flex flex-wrap items-center gap-4 border-b border-primary-100 bg-primary-50 px-4 py-2"
    >
      <span className="text-sm font-semibold text-neutral-800">
        {formatMessage(queue.selection.selected, { count })}
      </span>
      <button
        type="button"
        data-testid="rm-selection-clear"
        onClick={onClear}
        className={linkClass}
      >
        {queue.selection.clear}
      </button>
      {canSelectAll && (
        <button
          type="button"
          data-testid="rm-selection-all"
          disabled={selectingAll}
          onClick={onSelectAll}
          className={linkClass}
        >
          {selectingAll
            ? queue.selection.selectingAll
            : formatMessage(queue.selection.selectAll, { count: laneTotal })}
        </button>
      )}
      {selectAllFailed && (
        <span className="text-xs text-error-700">
          {queue.selection.selectAllFailed}
        </span>
      )}
      <span className="flex-1" />
      {tooMany && (
        <span data-testid="rm-selection-too-many" className="text-xs text-error-700">
          {formatMessage(queue.selection.tooMany, { max: BULK_MAX_JOBS })}
        </span>
      )}
      <Button
        variant="DARK"
        data-testid="rm-selection-mark"
        disabled={tooMany}
        onClick={onMark}
      >
        {formatMessage(
          target === 'printed'
            ? queue.selection.markPrinted
            : queue.selection.markShipped,
          { count },
        )}
      </Button>
    </div>
  );
}

function BulkOutcomeBanner({
  outcome,
  onDismiss,
}: {
  outcome: BulkOutcome;
  onDismiss: () => void;
}) {
  return (
    <div
      role="status"
      data-testid="rm-bulk-outcome"
      className="flex items-start justify-between gap-4 border-b border-neutral-100 bg-neutral-50 px-4 py-3 text-sm"
    >
      <div className="flex flex-col gap-1">
        {outcome.moved > 0 && (
          <span data-testid="rm-bulk-moved" className="font-semibold text-success-700">
            {formatMessage(queue.bulkResult.moved, {
              count: outcome.moved,
              to: stageLabel(outcome.toStage),
            })}
          </span>
        )}
        {outcome.refused.length > 0 && (
          <>
            <span className="font-semibold text-error-700">
              {formatMessage(queue.bulkResult.refused, {
                count: outcome.refused.length,
              })}
            </span>
            <ul className="list-disc pl-5 text-neutral-700">
              {outcome.refused.map((row) => (
                <li key={row.jobId} data-testid={`rm-bulk-refused-${row.jobId}`}>
                  <span className="font-medium">{row.name}</span> — {row.message}
                </li>
              ))}
            </ul>
          </>
        )}
      </div>
      <button
        type="button"
        onClick={onDismiss}
        className="text-sm font-semibold text-neutral-500 underline"
      >
        {queue.bulkResult.dismiss}
      </button>
    </div>
  );
}

function BulkConfirmModal({
  open,
  names,
  from,
  to,
  submitting,
  failed,
  onClose,
  onConfirm,
}: {
  open: boolean;
  names: string[];
  from: RmPrintStage;
  to: RmAdvanceStage;
  submitting: boolean;
  failed: boolean;
  onClose: () => void;
  onConfirm: () => void;
}) {
  const { names: shown, more } = confirmNames(names);
  const vars = {
    names: shown,
    more,
    from: stageLabel(from),
    to: stageLabel(to),
    count: names.length,
  };
  return (
    <DefaultModal
      title={formatMessage(
        names.length === 1
          ? queue.selection.confirmTitleOne
          : queue.selection.confirmTitle,
        { count: names.length, to: stageLabel(to).toLowerCase() },
      )}
      showModal={open}
      setShowModal={(show) => {
        if (!show) onClose();
      }}
      size="md"
    >
      {open && (
        <div className="flex flex-col gap-4" data-testid="rm-bulk-confirm">
          <p className="text-sm text-neutral-600">
            {formatMessage(
              more > 0 ? queue.selection.confirmMore : queue.selection.confirmBody,
              vars,
            )}
          </p>
          {failed && (
            <p role="alert" className="rounded-lg bg-red-50 p-3 text-sm text-red-800">
              {queue.selection.requestFailed}
            </p>
          )}
          <div className="flex justify-end gap-2">
            <Button variant="OUTLINED" disabled={submitting} onClick={onClose}>
              {queue.selection.keep}
            </Button>
            <Button
              variant="DARK"
              data-testid="rm-bulk-confirm-button"
              isProcessing={submitting}
              disabled={submitting}
              onClick={onConfirm}
            >
              {formatMessage(queue.selection.confirm, {
                count: names.length,
                to: stageLabel(to).toLowerCase(),
              })}
            </Button>
          </div>
        </div>
      )}
    </DefaultModal>
  );
}
